/**
 * Restore-safe transaction-contact re-linker.
 *
 * After a wallet restore, Evolu row IDs change but domain identifiers
 * (Lightning Addresses, Nostr pubkeys, BOLT12 offers) remain stable.
 * This module re-links orphaned transaction-contact references using
 * domain identifier matching.
 *
 * Also provides duplicate contact detection for CRDT sync dedup.
 *
 * @module features/contacts/utils/contact-relinker
 */

import type { Contact } from '@/types/contact';

/** Minimal transaction shape needed for re-linking. */
export interface TransactionForRelink {
  readonly id: string;
  readonly contactId: string | null;
  readonly lightningAddress: string | null;
  readonly nostrPubkey?: string | null;
  readonly bolt12Offer?: string | null;
}

/** A pair of contacts identified as potential duplicates. */
export interface DuplicatePair {
  readonly contactId1: string;
  readonly contactId2: string;
  readonly field: 'lightningAddress' | 'nostrPubkey' | 'bolt12Offer' | 'cashuAddress';
  readonly value: string;
}

/** Domain identifier fields used for matching. */
const DOMAIN_FIELDS = ['lightningAddress', 'nostrPubkey', 'bolt12Offer', 'cashuAddress'] as const;

type DomainField = (typeof DOMAIN_FIELDS)[number];

/**
 * Re-link orphaned transactions to restored contacts using domain identifiers.
 *
 * For each transaction with a contactId that doesn't match any current contact,
 * attempt to find a matching contact via shared domain identifiers
 * (lightningAddress, nostrPubkey, bolt12Offer).
 *
 * @param transactions - Transactions to check for orphaned contact references
 * @param contacts - Current contact list (post-restore with new IDs)
 * @returns Map of transactionId -> newContactId for transactions that were re-linked
 */
export function relinkTransactionsToContacts(
  transactions: readonly TransactionForRelink[],
  contacts: readonly Contact[],
): ReadonlyMap<string, string> {
  if (transactions.length === 0 || contacts.length === 0) {
    return new Map();
  }

  // Build lookup indexes: domain identifier value -> contact ID
  const byLightningAddr = new Map<string, string>();
  const byNostrPubkey = new Map<string, string>();
  const byBolt12 = new Map<string, string>();

  const validContactIds = new Set(contacts.map((c) => c.id));

  for (const contact of contacts) {
    if (contact.lightningAddress) {
      byLightningAddr.set(contact.lightningAddress.toLowerCase(), contact.id);
    }
    if (contact.nostrPubkey) {
      byNostrPubkey.set(contact.nostrPubkey.toLowerCase(), contact.id);
    }
    if (contact.bolt12Offer) {
      byBolt12.set(contact.bolt12Offer.toLowerCase(), contact.id);
    }
  }

  const relinks = new Map<string, string>();

  for (const tx of transactions) {
    // Skip transactions with no contactId (never linked)
    if (tx.contactId === null) {
      continue;
    }

    // Skip transactions whose contactId already matches a current contact
    if (validContactIds.has(tx.contactId)) {
      continue;
    }

    // Try to match via domain identifiers
    let newContactId: string | undefined;

    if (tx.lightningAddress) {
      newContactId = byLightningAddr.get(tx.lightningAddress.toLowerCase());
    }

    if (!newContactId && tx.nostrPubkey) {
      newContactId = byNostrPubkey.get(tx.nostrPubkey.toLowerCase());
    }

    if (!newContactId && tx.bolt12Offer) {
      newContactId = byBolt12.get(tx.bolt12Offer.toLowerCase());
    }

    if (newContactId) {
      relinks.set(tx.id, newContactId);
    }
  }

  return relinks;
}

/**
 * Find duplicate contacts by shared domain identifiers.
 *
 * Checks all contact pairs for overlapping Lightning Addresses, Nostr
 * pubkeys, BOLT12 offers, or Cashu addresses. Returns pairs with the
 * shared field name and value.
 *
 * @param contacts - Contact list to check for duplicates
 * @returns Array of duplicate pairs with the shared domain identifier
 */
export function findDuplicateContacts(contacts: readonly Contact[]): readonly DuplicatePair[] {
  if (contacts.length < 2) {
    return [];
  }

  const duplicates: DuplicatePair[] = [];

  for (const field of DOMAIN_FIELDS) {
    const seen = new Map<string, string>(); // value -> first contact ID

    for (const contact of contacts) {
      const value = contact[field as keyof Contact] as string | null;
      if (!value) {
        continue;
      }

      const normalizedValue = value.toLowerCase();
      const existingId = seen.get(normalizedValue);

      if (existingId) {
        duplicates.push({
          contactId1: existingId,
          contactId2: contact.id,
          field: field as DomainField,
          value,
        });
      } else {
        seen.set(normalizedValue, contact.id);
      }
    }
  }

  return duplicates;
}
